import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import ThemeToggle from '@/components/ThemeToggle';
import {
    ShieldCheck, Lock, Monitor, Clock, LogOut, X, Loader2, ArrowLeft,
    CheckCircle2, XCircle, Trash2, Eye, EyeOff, KeyRound, Copy, RefreshCw,
} from 'lucide-react';

const ROLE_ACCENT = {
    admin: '#7C6BEF',
    client: '#0EA5E9',
    provider: '#0EA5E9',
    inspector: '#00875A',
};

function fmt(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return iso; }
}

function browserLabel(ua) {
    if (!ua || ua === '-') return 'Desconocido';
    if (/edg\//i.test(ua)) return 'Edge';
    if (/chrome\//i.test(ua) && !/edg\//i.test(ua)) return 'Chrome';
    if (/safari\//i.test(ua) && !/chrome\//i.test(ua)) return 'Safari';
    if (/firefox\//i.test(ua)) return 'Firefox';
    if (/postman|curl|python|axios/i.test(ua)) return 'CLI / API';
    return ua.split(' ')[0];
}
function osLabel(ua) {
    if (!ua || ua === '-') return '';
    if (/windows/i.test(ua)) return 'Windows';
    if (/mac os|macintosh/i.test(ua)) return 'macOS';
    if (/android/i.test(ua)) return 'Android';
    if (/iphone|ipad|ios/i.test(ua)) return 'iOS';
    if (/linux/i.test(ua)) return 'Linux';
    return '';
}

export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const accent = ROLE_ACCENT[user?.role] || '#111';

    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    if (!user) return null;

    const backTo = `/${user.role}`;

    return (
        <div className="min-h-screen" style={{ background: 'var(--bg, #F7F7F9)' }} data-testid="profile-page">
            <header className="border-b bg-white" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button onClick={() => navigate(backTo)} data-testid="profile-back"
                        className="inline-flex items-center gap-2 text-sm font-bold hover:underline" style={{ color: accent }}>
                        <ArrowLeft className="w-4 h-4" /> Volver al panel
                    </button>
                    <div className="flex items-center gap-3">
                        <ThemeToggle />
                        <button onClick={logout} data-testid="profile-logout"
                            className="inline-flex items-center gap-2 px-3 py-2 border text-sm hover:bg-black/5"
                            style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                            <LogOut className="w-4 h-4" /> Cerrar sesión
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center font-black text-white text-2xl"
                        style={{ background: accent }}>
                        {(user.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>
                            {user.role}
                        </div>
                        <h1 className="font-display text-3xl font-black tracking-tight">{user.name}</h1>
                        <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                </div>

                <ChangePasswordSection accent={accent} />
                {user.role === 'admin' && <TwoFactorSection accent={accent} />}
                <ActiveSessionsSection accent={accent} onSessionRevoked={() => {}} />
                <LoginHistorySection accent={accent} />
            </main>
        </div>
    );
}

/* ---------------- Change Password ---------------- */
function ChangePasswordSection({ accent }) {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);

    const strength = getStrength(next);

    const submit = async (e) => {
        e.preventDefault();
        if (next !== confirm) { toast.error('Las contraseñas nuevas no coinciden'); return; }
        setSubmitting(true);
        try {
            await api.post('/auth/me/password', {
                current_password: current,
                new_password: next,
            });
            toast.success('Contraseña actualizada. Las otras sesiones fueron cerradas.');
            setCurrent(''); setNext(''); setConfirm('');
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al cambiar contraseña'));
        } finally { setSubmitting(false); }
    };

    return (
        <section className="p-6 border bg-white" style={{ borderColor: 'var(--border, #E5E7EB)' }} data-testid="profile-change-password">
            <SectionHeader accent={accent} icon={Lock} title="Cambiar contraseña"
                subtitle="Al cambiarla, todas las demás sesiones activas se cerrarán automáticamente." />
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <PasswordField label="Contraseña actual *" value={current} onChange={setCurrent}
                    show={showCurrent} onToggle={() => setShowCurrent(!showCurrent)}
                    autoComplete="current-password" testId="input-current-password" required />
                <div />
                <PasswordField label="Nueva contraseña *" value={next} onChange={setNext}
                    show={showNext} onToggle={() => setShowNext(!showNext)}
                    autoComplete="new-password" testId="input-new-password" required />
                <PasswordField label="Confirmar nueva contraseña *" value={confirm} onChange={setConfirm}
                    show={showNext} onToggle={() => setShowNext(!showNext)}
                    autoComplete="new-password" testId="input-confirm-password" required />
                <div className="md:col-span-2">
                    <StrengthBar score={strength.score} label={strength.label} />
                    <ul className="mt-2 text-xs text-gray-500 space-y-1">
                        <li>· Mínimo 10 caracteres</li>
                        <li>· Debe incluir mayúscula, minúscula, dígito y símbolo</li>
                        <li>· Distinta a la contraseña actual</li>
                    </ul>
                </div>
                <div className="md:col-span-2 flex justify-end">
                    <button type="submit" disabled={submitting || !current || !next || !confirm}
                        data-testid="submit-change-password"
                        className="text-white px-6 py-2.5 font-bold inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: accent }}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                        Actualizar contraseña
                    </button>
                </div>
            </form>
        </section>
    );
}

function PasswordField({ label, value, onChange, show, onToggle, autoComplete, testId, required }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">{label}</label>
            <div className="relative">
                <input
                    type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete} required={required}
                    data-testid={testId}
                    className="w-full px-3 py-2.5 pr-10 border outline-none"
                    style={{ borderColor: 'var(--border, #E5E7EB)', background: 'var(--card-bg, #fff)' }} />
                <button type="button" onClick={onToggle}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
}

function getStrength(pwd) {
    if (!pwd) return { score: 0, label: '' };
    let score = 0;
    if (pwd.length >= 10) score++;
    if (pwd.length >= 14) score++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    const labels = ['Muy débil', 'Débil', 'Aceptable', 'Buena', 'Fuerte', 'Muy fuerte'];
    return { score, label: labels[Math.min(score, 5)] };
}

function StrengthBar({ score, label }) {
    const colors = ['#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E', '#10B981'];
    const width = Math.min(score, 5) * 20;
    return (
        <div>
            <div className="h-2 bg-gray-200 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${width}%`, background: colors[Math.min(score, 5)] }} />
            </div>
            {label && <div className="text-xs mt-1" style={{ color: colors[Math.min(score, 5)] }}>{label}</div>}
        </div>
    );
}

/* ---------------- Two-Factor Auth (admin only) ---------------- */
function TwoFactorSection({ accent }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showEnroll, setShowEnroll] = useState(false);
    const [showDisable, setShowDisable] = useState(false);
    const [showRegenerate, setShowRegenerate] = useState(false);

    const load = () => {
        setLoading(true);
        api.get('/auth/mfa/status')
            .then((r) => setStatus(r.data))
            .catch(() => toast.error('No se pudo cargar el estado de 2FA'))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    return (
        <section className="p-6 border bg-white" style={{ borderColor: 'var(--border, #E5E7EB)' }} data-testid="profile-2fa-section">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <SectionHeader accent={accent} icon={KeyRound} title="Verificación en dos pasos (2FA)"
                    subtitle="Protege tu cuenta de administrador con un código adicional de tu app de autenticación (Google Authenticator, Authy)." />
                {!loading && status && (
                    status.enabled ? (
                        <span data-testid="mfa-status-enabled" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5" style={{ background: '#10B98120', color: '#10B981' }}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Activo
                        </span>
                    ) : (
                        <span data-testid="mfa-status-disabled" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 bg-gray-100 text-gray-500">
                            <XCircle className="w-3.5 h-3.5" /> Inactivo
                        </span>
                    )
                )}
            </div>

            {!loading && status && (
                <div className="mt-5">
                    {status.enabled ? (
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="text-sm text-gray-500">
                                Activado el {fmt(status.activated_at)} · <strong data-testid="mfa-recovery-remaining">{status.recovery_codes_remaining}</strong> código(s) de respaldo sin usar.
                            </div>
                            <div className="flex gap-2 ml-auto">
                                <button onClick={() => setShowRegenerate(true)} data-testid="mfa-regenerate-btn"
                                    className="text-xs px-3 py-2 border inline-flex items-center gap-1.5 hover:bg-black/5" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                                    <RefreshCw className="w-3.5 h-3.5" /> Regenerar códigos
                                </button>
                                <button onClick={() => setShowDisable(true)} data-testid="mfa-disable-btn"
                                    className="text-xs px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5">
                                    <X className="w-3.5 h-3.5" /> Desactivar 2FA
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setShowEnroll(true)} data-testid="mfa-enable-btn"
                            className="text-white px-6 py-2.5 font-bold inline-flex items-center gap-2" style={{ background: accent }}>
                            <ShieldCheck className="w-4 h-4" /> Activar verificación en dos pasos
                        </button>
                    )}
                </div>
            )}

            {showEnroll && <MfaEnrollModal onClose={() => setShowEnroll(false)} onDone={() => { setShowEnroll(false); load(); }} />}
            {showDisable && <MfaDisableModal onClose={() => setShowDisable(false)} onDone={() => { setShowDisable(false); load(); }} />}
            {showRegenerate && <MfaRegenerateModal onClose={() => setShowRegenerate(false)} />}
        </section>
    );
}

function ModalShell({ title, onClose, children, testId, hideClose }) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid={testId}>
            <div className="bg-white max-w-md w-full p-6 relative">
                {!hideClose && (
                    <button onClick={onClose} data-testid="mfa-modal-close" className="absolute top-4 right-4 text-gray-400 hover:text-black">
                        <X className="w-5 h-5" />
                    </button>
                )}
                <h3 className="font-display text-xl font-black mb-4">{title}</h3>
                {children}
            </div>
        </div>
    );
}

function MfaEnrollModal({ onClose, onDone }) {
    const [step, setStep] = useState('qr'); // qr -> verify -> backup
    const [qr, setQr] = useState(null);
    const [secret, setSecret] = useState('');
    const [code, setCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [ackSaved, setAckSaved] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.post('/auth/mfa/enroll')
            .then((r) => { setQr(r.data.qr_data_url); setSecret(r.data.secret); })
            .catch((err) => { toast.error(apiErrorMessage(err, 'No se pudo iniciar la activación')); onClose(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verify = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post('/auth/mfa/activate', { code });
            setRecoveryCodes(data.recovery_codes);
            setStep('backup');
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Código inválido'));
        } finally { setSubmitting(false); }
    };

    const copyAll = () => {
        navigator.clipboard.writeText(recoveryCodes.join('\n'));
        toast.success('Códigos copiados al portapapeles');
    };

    return (
        <ModalShell title="Activar verificación en dos pasos" onClose={onClose} testId="mfa-enroll-modal" hideClose={step === 'backup'}>
            {step === 'qr' && (
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">Escanea este código con Google Authenticator, Authy o una app similar.</p>
                    {qr ? (
                        <div className="flex justify-center"><img src={qr} alt="Código QR de activación 2FA" data-testid="mfa-qr-image" className="w-48 h-48 border" /></div>
                    ) : (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                    )}
                    {secret && (
                        <div className="text-center">
                            <div className="text-xs text-gray-400 mb-1">¿No puedes escanear? Ingresa este código manualmente:</div>
                            <code data-testid="mfa-manual-secret" className="text-sm font-mono bg-gray-100 px-3 py-1.5 inline-block tracking-wider">{secret}</code>
                        </div>
                    )}
                    <button onClick={() => setStep('verify')} disabled={!qr} data-testid="mfa-enroll-continue-btn"
                        className="w-full bg-black text-white py-3 font-bold disabled:opacity-50">
                        Ya lo escaneé, continuar
                    </button>
                </div>
            )}

            {step === 'verify' && (
                <form onSubmit={verify} className="space-y-4">
                    <p className="text-sm text-gray-600">Ingresa el código de 6 dígitos que muestra tu app para confirmar la activación.</p>
                    <input autoFocus required value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456" inputMode="numeric" data-testid="mfa-activate-code-input"
                        className="w-full border border-gray-300 px-4 py-3 text-center font-mono text-lg tracking-widest outline-none focus:border-black" />
                    <button type="submit" disabled={submitting || code.length !== 6} data-testid="mfa-activate-submit-btn"
                        className="w-full bg-black text-white py-3 font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verificar y activar
                    </button>
                </form>
            )}

            {step === 'backup' && (
                <div className="space-y-4">
                    <p className="text-sm font-bold text-red-600">Guarda estos 10 códigos de respaldo en un lugar seguro. Solo se muestran una vez.</p>
                    <p className="text-xs text-gray-500">Si pierdes tu celular, cada código te permite iniciar sesión una sola vez en lugar del código de 6 dígitos.</p>
                    <p className="text-xs text-amber-600 font-medium">La verificación en dos pasos ya está activa en tu cuenta — no puedes cerrar esta ventana sin confirmar que guardaste los códigos.</p>
                    <div data-testid="mfa-recovery-codes" className="grid grid-cols-2 gap-2 bg-gray-50 border p-4 font-mono text-sm">
                        {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
                    </div>
                    <button onClick={copyAll} data-testid="mfa-copy-codes-btn"
                        className="w-full border py-2.5 font-medium inline-flex items-center justify-center gap-2 hover:bg-black/5">
                        <Copy className="w-4 h-4" /> Copiar todos los códigos
                    </button>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={ackSaved} onChange={(e) => setAckSaved(e.target.checked)} data-testid="mfa-ack-saved-checkbox" className="mt-1" />
                        Ya guardé mis códigos de respaldo en un lugar seguro
                    </label>
                    <button onClick={onDone} disabled={!ackSaved} data-testid="mfa-finish-btn"
                        className="w-full bg-black text-white py-3 font-bold disabled:opacity-50">
                        Listo, activación completa
                    </button>
                </div>
            )}
        </ModalShell>
    );
}

function MfaDisableModal({ onClose, onDone }) {
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post('/auth/mfa/disable', { password, code });
            toast.success('2FA desactivado');
            onDone();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo desactivar'));
        } finally { setSubmitting(false); }
    };

    return (
        <ModalShell title="Desactivar verificación en dos pasos" onClose={onClose} testId="mfa-disable-modal">
            <form onSubmit={submit} className="space-y-4">
                <p className="text-sm text-gray-600">Confirma tu contraseña y un código vigente (o de respaldo) para desactivar 2FA.</p>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Contraseña actual" autoComplete="current-password" data-testid="mfa-disable-password-input"
                    className="w-full border border-gray-300 px-4 py-3 outline-none focus:border-black" />
                <input required value={code} onChange={(e) => setCode(e.target.value)}
                    placeholder="Código de 6 dígitos o de respaldo" data-testid="mfa-disable-code-input"
                    className="w-full border border-gray-300 px-4 py-3 font-mono outline-none focus:border-black" />
                <button type="submit" disabled={submitting} data-testid="mfa-disable-submit-btn"
                    className="w-full bg-red-600 text-white py-3 font-bold disabled:opacity-50">
                    Desactivar 2FA
                </button>
            </form>
        </ModalShell>
    );
}

function MfaRegenerateModal({ onClose }) {
    const [password, setPassword] = useState('');
    const [codes, setCodes] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post('/auth/mfa/regenerate-codes', { password });
            setCodes(data.recovery_codes);
        } catch (err) {
            toast.error(apiErrorMessage(err, 'No se pudo regenerar'));
        } finally { setSubmitting(false); }
    };

    return (
        <ModalShell title="Regenerar códigos de respaldo" onClose={onClose} testId="mfa-regenerate-modal">
            {!codes ? (
                <form onSubmit={submit} className="space-y-4">
                    <p className="text-sm text-gray-600">Esto invalida tus 10 códigos anteriores y genera 10 nuevos. Confirma tu contraseña.</p>
                    <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="Contraseña actual" autoComplete="current-password" data-testid="mfa-regenerate-password-input"
                        className="w-full border border-gray-300 px-4 py-3 outline-none focus:border-black" />
                    <button type="submit" disabled={submitting} data-testid="mfa-regenerate-submit-btn"
                        className="w-full bg-black text-white py-3 font-bold disabled:opacity-50">
                        Regenerar códigos
                    </button>
                </form>
            ) : (
                <div className="space-y-4">
                    <p className="text-sm font-bold text-red-600">Nuevos códigos — los anteriores ya no funcionan. Guárdalos ahora.</p>
                    <div data-testid="mfa-new-recovery-codes" className="grid grid-cols-2 gap-2 bg-gray-50 border p-4 font-mono text-sm">
                        {codes.map((c) => <div key={c}>{c}</div>)}
                    </div>
                    <button onClick={onClose} data-testid="mfa-regenerate-done-btn" className="w-full bg-black text-white py-3 font-bold">
                        Listo
                    </button>
                </div>
            )}
        </ModalShell>
    );
}

/* ---------------- Sessions ---------------- */
function ActiveSessionsSection({ accent }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revoking, setRevoking] = useState(null);

    const load = () => {
        setLoading(true);
        api.get('/auth/me/sessions')
            .then((r) => setSessions(r.data.sessions || []))
            .catch(() => toast.error('No se pudieron cargar las sesiones'))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const revoke = async (sid) => {
        if (!window.confirm('¿Cerrar esta sesión?')) return;
        setRevoking(sid);
        try {
            await api.delete(`/auth/me/sessions/${sid}`);
            toast.success('Sesión cerrada');
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al cerrar sesión'));
        } finally { setRevoking(null); }
    };

    const revokeOthers = async () => {
        if (!window.confirm('¿Cerrar TODAS las otras sesiones? Solo permanecerá activa la actual.')) return;
        try {
            const r = await api.post('/auth/me/sessions/revoke-others');
            toast.success(`${r.data.revoked_count} sesión(es) cerradas`);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error'));
        }
    };

    const active = sessions.filter((s) => !s.revoked);

    return (
        <section className="p-6 border bg-white" style={{ borderColor: 'var(--border, #E5E7EB)' }} data-testid="profile-sessions">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <SectionHeader accent={accent} icon={Monitor} title="Sesiones activas"
                    subtitle={`${active.length} sesión(es) abiertas actualmente.`} />
                {active.length > 1 && (
                    <button onClick={revokeOthers} data-testid="revoke-others-btn"
                        className="text-sm px-3 py-2 border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
                        <LogOut className="w-4 h-4" /> Cerrar todas las otras
                    </button>
                )}
            </div>

            {loading ? (
                <div className="mt-5 text-sm text-gray-500">Cargando...</div>
            ) : sessions.length === 0 ? (
                <div className="mt-5 text-sm text-gray-500">No hay sesiones registradas.</div>
            ) : (
                <div className="mt-5 border" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-left" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Dispositivo</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">IP</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Iniciada</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Última actividad</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Estado</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s) => (
                                <tr key={s.id} className="border-t" style={{ borderColor: 'var(--border, #E5E7EB)' }} data-testid={`session-row-${s.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium">
                                            {browserLabel(s.user_agent)}{osLabel(s.user_agent) && ` · ${osLabel(s.user_agent)}`}
                                        </div>
                                        {s.is_current && (
                                            <span className="inline-block mt-1 text-[10px] uppercase font-bold px-1.5 py-0.5" style={{ background: `${accent}20`, color: accent }}>
                                                Esta sesión
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs">{s.ip}</td>
                                    <td className="px-4 py-3 text-xs text-gray-500">{fmt(s.created_at)}</td>
                                    <td className="px-4 py-3 text-xs text-gray-500">{fmt(s.last_seen_at)}</td>
                                    <td className="px-4 py-3">
                                        {s.revoked ? (
                                            <span className="inline-flex items-center gap-1 text-xs text-gray-400"><XCircle className="w-3.5 h-3.5" /> Cerrada</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#10B981' }}><CheckCircle2 className="w-3.5 h-3.5" /> Activa</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {!s.revoked && !s.is_current && (
                                            <button onClick={() => revoke(s.id)} disabled={revoking === s.id}
                                                data-testid={`revoke-session-${s.id}`}
                                                className="text-xs px-2 py-1 border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50">
                                                {revoking === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                                                Cerrar
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

/* ---------------- Login History ---------------- */
function LoginHistorySection({ accent }) {
    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/auth/me/login-history')
            .then((r) => setAttempts(r.data.attempts || []))
            .catch(() => toast.error('No se pudo cargar el historial'))
            .finally(() => setLoading(false));
    }, []);

    const reasonLabel = (reason) => {
        const map = {
            login: 'Inicio exitoso',
            register: 'Registro',
            wrong_password: 'Contraseña incorrecta',
            user_not_found: 'Usuario no encontrado',
            inactive: 'Cuenta desactivada',
            wrong_current_password: 'Contraseña actual incorrecta',
            password_changed: 'Contraseña cambiada',
        };
        return map[reason] || reason || '—';
    };

    return (
        <section className="p-6 border bg-white" style={{ borderColor: 'var(--border, #E5E7EB)' }} data-testid="profile-login-history">
            <SectionHeader accent={accent} icon={Clock} title="Historial de inicios de sesión"
                subtitle="Últimos 50 intentos. Si ves accesos que no reconoces, cambia tu contraseña de inmediato." />

            {loading ? (
                <div className="mt-5 text-sm text-gray-500">Cargando...</div>
            ) : attempts.length === 0 ? (
                <div className="mt-5 text-sm text-gray-500">Sin registros aún.</div>
            ) : (
                <div className="mt-5 border max-h-[420px] overflow-auto" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-white z-10">
                            <tr className="border-b text-left" style={{ borderColor: 'var(--border, #E5E7EB)' }}>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Fecha / Hora</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Resultado</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Detalle</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">IP</th>
                                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider">Dispositivo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attempts.map((a) => (
                                <tr key={a.id} className="border-t" style={{ borderColor: 'var(--border, #E5E7EB)' }} data-testid={`login-attempt-${a.id}`}>
                                    <td className="px-4 py-3 text-xs">{fmt(a.created_at)}</td>
                                    <td className="px-4 py-3">
                                        {a.success ? (
                                            <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#10B981' }}>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Exitoso
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-xs text-red-500">
                                                <XCircle className="w-3.5 h-3.5" /> Fallido
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-600">{reasonLabel(a.reason)}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{a.ip}</td>
                                    <td className="px-4 py-3 text-xs text-gray-500">
                                        {browserLabel(a.user_agent)}{osLabel(a.user_agent) && ` · ${osLabel(a.user_agent)}`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

/* ---------------- shared ---------------- */
function SectionHeader({ accent, icon: Icon, title, subtitle }) {
    return (
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: `${accent}18`, color: accent }}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>Seguridad</div>
                <h2 className="font-display text-lg font-black">{title}</h2>
                {subtitle && <p className="text-sm text-gray-500 mt-1 max-w-xl">{subtitle}</p>}
            </div>
        </div>
    );
}
